import { useEffect, useMemo, useRef, useState } from 'react';

import { Badge } from '@components/badge';
import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { useUnsavedChangesModal } from '@components/generic-modals';
import { IconGear } from '@components/icons/gear';
import { IconUndo } from '@components/icons/undo';
import { RelativeSpinner } from '@components/loading-spinner';
import { Separator } from '@components/separator';
import {
  forceHideSidebar,
  Sidebar,
  useSidebar,
  useSidebarEventHandlers,
} from '@components/sidebar';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';
import { useEvent } from '@hooks/use-event';
import { useScopedTranslate } from '@locale/translate';

import { TriggerType } from './api';
import { useCreateJobTrigger, useJobJourneyConfig, useSaveJobJourneyConfig } from './hooks';

import { journeyListStrings } from '../components/list-strings';
import { FieldConfigForm } from '../field-config/field-config-form';
import {
  areDraftsEqual,
  baselineByDraftKey,
  buildDraftFromResolvedFields,
  buildJobOverrideFromDraft,
  isFieldOverriddenFromGlobal,
  resetFieldToGlobal,
  storedSectionFor,
} from '../field-config/serialize';
import { FieldConfigDraft, JourneyAttribute, StoredFieldConfig } from '../field-config/types';
import { useVariableGroups } from '../helpers';
import { useCompanyTravellerAttributes } from '../hooks';

type TriggerConfigSidebarProps = {
  jobId: number;
  journeyId: string;
  journeyTitle: string;
  /**
   * Set when the panel was opened by picking a journey that is not a trigger
   * yet: Save creates the trigger, and every dismissal path adds nothing.
   * Absent when editing the config of a trigger that already exists.
   */
  pendingTrigger?: { triggerType: TriggerType; jobStage: number | null };
  /**
   * Called whenever the panel is dismissed — whether via Cancel, a successful
   * Save, backdrop click, or Escape. The triggers sidebar uses it to swap
   * itself back in (only one right-side panel exists at a time).
   */
  onClose?: () => void;
};

export const useTriggerConfigSidebar = () => {
  return useSidebar(TriggerConfigSidebar, {
    variant: 'REGULAR',
    hasBackdrop: true,
  });
};

/**
 * Per-job journey configuration sidebar.
 *
 * Opens pre-filled with the resolved fields (global merged with any per-job
 * override) returned by GET jobs/<job_id>/journeys/<journey_id>/config/.
 * Reuses the shared FieldConfigForm component from field-config/ so the edit
 * experience is identical to the global config sidebar.
 *
 * Opened with `pendingTrigger` the panel is the second half of adding a
 * journey to a stage or outcome: nothing has been persisted yet, so Save is
 * what creates the trigger (then writes any override), and Cancel/Escape
 * leave the job untouched. Without it the trigger already exists and Save
 * only writes the override.
 *
 * On save the draft is diffed against the resolved baseline and only the
 * changed keys are PUT as the per-job field_config — inherited global
 * values are never copied into the job row and the global config is never
 * touched. Base mappings (name/email/phone) stay on the global config; this
 * panel edits attribute fields only.
 */
export const TriggerConfigSidebar = ({
  jobId,
  journeyId,
  journeyTitle,
  pendingTrigger,
  onClose,
}: TriggerConfigSidebarProps) => {
  const { t } = useScopedTranslate(locale);
  const configQuery = useJobJourneyConfig(jobId, journeyId);
  const saveConfig = useSaveJobJourneyConfig({ jobId, journeyId });
  const createTrigger = useCreateJobTrigger(jobId);
  const showUnsavedChangesModal = useUnsavedChangesModal();
  const { showError } = useToaster();
  const { setOnBeforeChange } = useSidebarEventHandlers();

  const [draft, setDraft] = useState<FieldConfigDraft | null>(null);
  // Protects an in-progress edit from a background refetch (window focus, or
  // a global-config save invalidating this query) — it exists only for that
  // guard, real dirty-tracking is the isDirty comparison below.
  const [hasEdited, setHasEdited] = useState(false);
  // Short ids the user has explicitly reset to global since the draft was
  // loaded. Append-only — see handleSave for how an edit made after a reset
  // drops a field back out of this set.
  const [resetShortIds, setResetShortIds] = useState<Set<string>>(new Set());
  // Bumped per shortId on reset so FieldConfigForm remounts that row —
  // uncontrolled editors otherwise keep showing the pre-reset text while the
  // draft (and therefore the save) already carries the global value.
  const [resetGenerations, setResetGenerations] = useState<Record<string, number>>({});
  const baselineDraftRef = useRef<FieldConfigDraft | null>(null);
  const isSavingRef = useRef(false);

  // Seed the draft once on first successful data load. A background refetch
  // only rebuilds when the draft is still untouched, so in-progress edits
  // are never silently discarded by a window-focus refetch.
  // Company-wide catalog, so its own query rather than the per-journey config
  // response; `source: 'traveller'` routes these decisions to the stored
  // traveller_fields section (their short_ids can collide with journey ones).
  const travellerAttributesQuery = useCompanyTravellerAttributes();
  const travellerAttrs = useMemo<JourneyAttribute[]>(
    () =>
      (travellerAttributesQuery.data ?? []).map((attribute) => ({
        ...attribute,
        source: 'traveller' as const,
      })),
    [travellerAttributesQuery.data],
  );

  const configData = configQuery.data;
  useEffect(() => {
    if (!configData) {
      return;
    }
    if (draft !== null && hasEdited) {
      return;
    }
    // Same wait as the global panel: seeding before the traveller catalog
    // arrives builds a draft with no traveller rows in it.
    if (!travellerAttributesQuery.isFetched) {
      return;
    }
    const nextDraft = buildDraftFromResolvedFields(
      configData.resolved_fields,
      [...configData.attributes, ...travellerAttrs],
      configData.resolved_traveller_fields ?? [],
      configData.start_journey,
    );
    baselineDraftRef.current = nextDraft;
    setDraft(nextDraft);
    setHasEdited(false);
    setResetShortIds(new Set());
    setResetGenerations({});
    // Deliberately not depending on `travellerAttrs`: it is a derived array, so
    // its identity changes whenever the query hands back a new `data` reference,
    // and re-running this effect calls setDraft — which re-renders, re-derives,
    // and loops. `isFetched` flipping false -> true is the one transition that
    // has to re-seed.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [configData, travellerAttributesQuery.isFetched]);

  // Ensure onClose runs for every dismissal path — including backdrop click
  // and Escape, which call hideRightSidebar directly and skip the Cancel
  // handler. We track whether onClose has already been called explicitly so
  // that Cancel / Save and unmount don't double-fire.
  const didCallOnClose = useRef(false);
  const callOnClose = () => {
    if (!didCallOnClose.current) {
      didCallOnClose.current = true;
      onClose?.();
    }
  };
  useEffect(() => {
    return () => {
      callOnClose();
    };
  }, []); // oxlint-disable-line react-hooks/exhaustive-deps

  // Job context: job + form variable groups stay available.
  const variableGroups = useVariableGroups({ jobId });
  const fileVariableGroups = useVariableGroups({ jobId, types: ['file'] });
  const fileMultiVariableGroups = useVariableGroups({ jobId, types: ['file', 'file_multi'] });

  const fieldMappingId = `job-journey-config-${jobId}-${journeyId}`;

  const attrs = useMemo<JourneyAttribute[]>(
    () => [...(configQuery.data?.attributes ?? []), ...travellerAttrs],
    [configQuery.data?.attributes, travellerAttrs],
  );

  // Keyed by draft key, spanning both catalogs — the draft is keyed that way,
  // and a shared short_id would otherwise read the wrong baseline.
  const resolvedByShortId = useMemo(
    () =>
      baselineByDraftKey(
        configQuery.data?.resolved_fields ?? [],
        configQuery.data?.resolved_traveller_fields ?? [],
      ),
    [configQuery.data],
  );

  // Drives the "Overridden" badge and the per-field reset button — live
  // against the draft as the user edits it, not only the persisted job override.
  const overriddenShortIds = useMemo(() => {
    if (!draft) {
      return new Set<string>();
    }
    const shortIds = new Set<string>();
    for (const [shortId, entry] of Object.entries(draft.fields)) {
      if (isFieldOverriddenFromGlobal(entry, resolvedByShortId.get(shortId))) {
        shortIds.add(shortId);
      }
    }
    return shortIds;
  }, [draft, resolvedByShortId]);

  // The global value this job inherits when it stores no start_journey key of
  // its own; toggling back to it is what clears the override.
  const globalStartJourney = configQuery.data?.global_start_journey ?? true;
  const isStartJourneyOverridden = draft !== null && draft.startJourney !== globalStartJourney;

  // A reset counts as dirty even when it produces a draft identical to the
  // baseline, because it changes what gets stored (dropping the override key
  // entirely rather than pinning it to a value that happens to match) — but
  // only when the job config actually stores a key to drop. Resetting a field
  // that was only edited in this session saves exactly what is already stored,
  // so it must not light Save up (QA).
  const resetDropsStoredOverride = useMemo(() => {
    const stored = configQuery.data?.job_field_config;
    if (!stored || !draft || resetShortIds.size === 0) {
      return false;
    }
    return [...resetShortIds].some((key) => {
      const entry = draft.fields[key];
      const shortId = entry?.shortId ?? key;
      return (stored[storedSectionFor(entry?.source)] ?? {})[shortId] !== undefined;
    });
  }, [configQuery.data, draft, resetShortIds]);

  const isDirty =
    draft !== null &&
    (resetDropsStoredOverride ||
      baselineDraftRef.current === null ||
      !areDraftsEqual(draft, baselineDraftRef.current));

  // Only when the journey has fields to offer — a journey with no attributes
  // at all already gets FieldConfigForm's own "nothing configurable" message.
  const hasNoIncludedFields =
    draft !== null &&
    attrs.length > 0 &&
    Object.values(draft.fields).every((entry) => !entry.included);

  // Adding a journey commits on Save, so an untouched panel still has work to
  // do; editing an existing trigger's config does not.
  const canSave = draft !== null && (pendingTrigger !== undefined || isDirty);

  const handleSave = async () => {
    // A ref, not saveConfig.isLoading: both `disabled` and isLoading are React
    // state that only settle on a later render, so a fast double-click gets
    // through them and fires the mutation twice. The ref latches synchronously.
    if (!draft || !configQuery.data || isSavingRef.current || !canSave) {
      return;
    }
    // A field the user reset and then edited again diverges from global
    // again, so it drops out of the reset list and is stored as a normal
    // override instead of being dropped from the job config.
    const resetIdsToDrop = [...resetShortIds].filter((shortId) => {
      const entry = draft.fields[shortId];
      return (
        entry !== undefined && !isFieldOverriddenFromGlobal(entry, resolvedByShortId.get(shortId))
      );
    });
    // buildJobOverrideFromDraft throws on a malformed variables shape — a
    // programming-error invariant that the editors never produce, but guard it
    // so it surfaces as the standard error toast instead of an uncaught render.
    let fieldConfig: StoredFieldConfig | null = null;
    if (isDirty) {
      try {
        fieldConfig = buildJobOverrideFromDraft(
          draft,
          configQuery.data.resolved_fields,
          configQuery.data.job_field_config ?? {},
          resetIdsToDrop,
          configQuery.data.resolved_traveller_fields ?? [],
          configQuery.data.global_start_journey,
        );
      } catch {
        showError(t('saveConfigError'));
        return;
      }
    }
    isSavingRef.current = true;
    try {
      // Ordered, not parallel: the override belongs to a trigger, so a failed
      // create must not leave a job override behind for a journey that was
      // never added. Both hooks raise their own error toast.
      if (pendingTrigger) {
        await createTrigger.mutateAsync({
          journeyId,
          triggerType: pendingTrigger.triggerType,
          jobStage: pendingTrigger.jobStage,
        });
      }
      if (fieldConfig) {
        await saveConfig.mutateAsync(fieldConfig);
      }
      setOnBeforeChange(undefined);
      forceHideSidebar();
      callOnClose();
    } catch {
      // Stay open so the work is not lost and the user can retry.
    } finally {
      isSavingRef.current = false;
    }
  };

  // Every deliberate close path must clear the guard before hiding — hideSidebar()
  // routes through onBeforeChange, which would re-open the prompt in a loop.
  const closeAndDiscard = () => {
    setOnBeforeChange(undefined);
    forceHideSidebar();
    callOnClose();
  };

  // useEvent, not a plain closure: the onBeforeChange guard below is registered
  // once on the clean -> dirty transition and would otherwise keep calling that
  // render's handleSave, saving the draft as it looked at the first edit and
  // silently dropping every edit made after it.
  //
  // Deferred by a tick because one dismissal path is the Escape key: the modal
  // is a HeadlessUI Dialog that closes itself on Escape and registers its
  // key listener as soon as it mounts, so opening it synchronously from the
  // keydown handler lets that same keypress continue bubbling into the fresh
  // listener and close it again — Escape would appear to do nothing at all.
  const promptUnsavedChanges = useEvent(() => {
    setTimeout(() => {
      showUnsavedChangesModal({
        onSave: handleSave,
        onDiscard: closeAndDiscard,
      });
    });
  });

  // Cancel and the header close button prompt when there are pending edits, so
  // a mis-click cannot silently throw away a part-built override. Escape and
  // backdrop click both reach hideRightSidebar() -> handleHideByPosition ->
  // onBeforeChange, so the effect below covers those paths too.
  const handleCancel = () => {
    if (!isDirty) {
      closeAndDiscard();
      return;
    }
    promptUnsavedChanges();
  };

  useEffect(() => {
    if (isDirty) {
      setOnBeforeChange(() => {
        promptUnsavedChanges();
        return false;
      });
    } else {
      setOnBeforeChange(undefined);
    }
    return () => setOnBeforeChange(undefined);
  }, [isDirty]); // oxlint-disable-line react-hooks/exhaustive-deps

  const handleDraftChange = (nextDraft: FieldConfigDraft) => {
    setDraft(nextDraft);
    setHasEdited(true);
  };

  const handleStartJourneyChange = (isOn: boolean) => {
    setDraft((prev) => (prev ? { ...prev, startJourney: isOn } : prev));
    setHasEdited(true);
  };

  const handleResetField = (shortId: string) => {
    if (!draft) {
      return;
    }
    const baseline = resolvedByShortId.get(shortId);
    if (!baseline) {
      return;
    }
    setDraft(resetFieldToGlobal(draft, shortId, baseline));
    setHasEdited(true);
    setResetShortIds((prev) => new Set(prev).add(shortId));
    setResetGenerations((prev) => ({ ...prev, [shortId]: (prev[shortId] ?? 0) + 1 }));
  };

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        title={journeyTitle}
        icon={IconGear}
        iconColor="purple100"
        onClose={handleCancel}
      />
      <Sidebar.Content data-cy="journey-config-sidebar">
        {configQuery.isLoading ? (
          <RelativeSpinner />
        ) : configQuery.isError ? (
          <Stack space={2}>
            <Text font="regular14" color="textSecondary" text={t('errorDescription')} />
            <Button variant="secondary" text={t('retry')} onClick={() => configQuery.refetch()} />
          </Stack>
        ) : draft !== null ? (
          <Stack space={4}>
            {pendingTrigger && <Text font="regular14" color="textPrimary" text={t('addOnSave')} />}
            <Text font="regular14" color="textSecondary" text={t('description')} />
            {hasNoIncludedFields && (
              <Text font="regular14" color="textHelper" text={t('noFieldsConfigured')} />
            )}
            <FieldConfigForm
              attrs={attrs}
              draft={draft}
              variableGroups={variableGroups}
              fileVariableGroups={fileVariableGroups}
              fileMultiVariableGroups={fileMultiVariableGroups}
              fieldMappingId={fieldMappingId}
              onChange={handleDraftChange}
              overriddenShortIds={overriddenShortIds}
              onResetField={handleResetField}
              remountKeys={resetGenerations}
            />
            <Separator />
            <Stack orientation="horizontal" justify="space-between" align="center" space={3}>
              <Stack space={1}>
                <Stack orientation="horizontal" align="center" space={2}>
                  <Text font="semibold14" text={t('startJourneyWhenAdded')} />
                  {isStartJourneyOverridden && <Badge variant="blue" text={t('overridden')} />}
                </Stack>
                <Text
                  font="regular14"
                  color="textSecondary"
                  text={draft.startJourney ? t('core.yes') : t('core.no')}
                />
              </Stack>
              <Stack orientation="horizontal" align="center" space={2}>
                {isStartJourneyOverridden && (
                  <Button
                    variant="ghost"
                    size="sm"
                    iconLeft={<IconUndo size={16} />}
                    tooltip={t('resetToGlobal')}
                    aria-label={t('resetStartJourneyLabel')}
                    onClick={() => handleStartJourneyChange(globalStartJourney)}
                  />
                )}
                <Switch
                  label={t('startJourneyWhenAdded')}
                  labelVisible={false}
                  value={draft.startJourney}
                  onChange={handleStartJourneyChange}
                />
              </Stack>
            </Stack>
          </Stack>
        ) : null}
      </Sidebar.Content>
      <Sidebar.Footer>
        <ButtonGroup align="right">
          <Button variant="secondary" text={t('core.cancel')} onClick={handleCancel} />
          <Button
            variant="primary-blue"
            text={t('core.save')}
            onClick={handleSave}
            disabled={saveConfig.isLoading || createTrigger.isLoading || !canSave}
          />
        </ButtonGroup>
      </Sidebar.Footer>
    </Sidebar>
  );
};

const locale = {
  en: {
    ...journeyListStrings.en,
    addOnSave: 'This journey is added when you save.',
    description:
      'Fields inherit the global journey configuration. Change a field here to override it for this job only.',
    noFieldsConfigured:
      'No fields are configured for this journey yet. Add the fields this job should collect.',
    errorDescription: 'Failed to load journey configuration. Please try again.',
    retry: 'Retry',
    startJourneyWhenAdded: 'Start journey when added',
    overridden: 'Overridden',
    resetToGlobal: 'Reset to global configuration',
    resetStartJourneyLabel: 'Reset "Start journey when added" to global configuration',
  },
  is: {
    ...journeyListStrings.is,
    addOnSave: 'Þessu Journey-ferli verður bætt við þegar þú vistar.',
    description:
      'Reitirnir nota almennar stillingar Journey-ferlisins. Breytingar sem þú gerir hér gilda aðeins fyrir þetta starf.',
    noFieldsConfigured:
      'Engir reitir hafa verið valdir fyrir þetta Journey-ferli. Bættu við þeim reitum sem á að fylla út fyrir þetta starf.',
    errorDescription: 'Ekki tókst að sækja stillingar Journey-ferlisins. Reyndu aftur.',
    retry: 'Reyna aftur',
    startJourneyWhenAdded: 'Ræsa Journey-ferli þegar umsækjanda er bætt við',
    overridden: 'Sérstilling',
    resetToGlobal: 'Nota almennar stillingar aftur',
    resetStartJourneyLabel:
      'Nota almennu stillinguna aftur fyrir „Ræsa Journey-ferli þegar umsækjanda er bætt við“',
  },
};
